import React, {
    useCallback,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { compose } from 'redux';
import { IconButton } from '@mui/material';
import { withStyles } from '@mui/styles';
import CopyAllIcon from '@mui/icons-material/CopyAll';

const COPY_BUTTON_PADDING = 20;

const styles = theme => ({
    body: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        cursor: 'auto',
        '&:hover': {
            color: theme.palette.secondary.main,
            cursor: 'pointer',
            textShadow: '0 0 0',
        },
    },
    fakeContainer: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',

        '&:hover $iconButton': {
            opacity: 1,
            transition: 'opacity 0.6s',
            transitionDelay: '0.6s',
        },
    },
    fakeValue: { position: 'absolute', left: 0, opacity: 0, zIndex: -999, padding: 'inherit' },
    fakeValueNotCell: { width: '100%', position: 'absolute', left: 0, opacity: 0, zIndex: -999, padding: 'inherit' },
    iconButton: { position: 'absolute', top: 0, opacity: 0, width: 32, height: 32, padding: 8 },
});

const WithCopyButton = props => {
    const [iconStyle, setIconStyle] = useState({});
    const [isValueTruncated, setIsValueTruncated] = useState(null);
    const containerRef = useRef(null);
    const valueRef = useRef(null);

    const setCopyButtonStyles = (containerRef, valueRef) => {
        const containerWidth = containerRef.current?.offsetWidth;
        const valueWidth = valueRef.current?.offsetWidth + COPY_BUTTON_PADDING;
        const isValueBiggerThanContainer = valueWidth >= containerWidth;

        if (isValueBiggerThanContainer !== isValueTruncated) {
            const computedIconStyle =
                isValueBiggerThanContainer ?
                    { right: 0 } : { left: valueRef.current?.offsetWidth - COPY_BUTTON_PADDING };
            setIconStyle(computedIconStyle);
            setIsValueTruncated(isValueBiggerThanContainer);
        }
    };

    useLayoutEffect(() => {
        setCopyButtonStyles(containerRef, valueRef);
    }, []);

    const onMouseEnter = () => {
        setCopyButtonStyles(containerRef, valueRef);
    };

    const onClickCopyButton = useCallback(e => {
        e.stopPropagation();
        navigator.clipboard.writeText(props.title);
    }, [props.title]);

    return (
        <>
            {props.children}
            <span
                ref={valueRef}
                className={props.notCell ? props.classes.fakeValueNotCell : props.classes.fakeValue}
            >
                {props.children}
            </span>
            <div
                onMouseEnter={onMouseEnter}
                ref={containerRef}
                className={props.classes.fakeContainer}
            >
                {props.title && (
                    <IconButton
                        style={iconStyle}
                        aria-label="copy"
                        size="small"
                        color="accent"
                        className={props.classes.iconButton}
                        onClick={onClickCopyButton}
                    >
                        <CopyAllIcon />
                    </IconButton>
                )}
            </div>
        </>
    );
};

export default compose(withStyles(styles))(React.memo(WithCopyButton));
